import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../Common/Layout/Header";
import {
  RiCheckboxCircleLine,
  RiErrorWarningLine,
  RiBusLine,
  RiUserLine,
  RiMapPinLine,
  RiCalendarLine,
  RiPrinterLine,
  RiDownloadLine,
  RiTimeLine,
  RiCloseCircleLine,
  RiMoneyDollarCircleLine,
  RiFileList3Line,
  RiArrowLeftRightLine,
  RiMailLine,
  RiWhatsappLine,
  RiInformationLine,
  RiAlertLine,
  RiRefundLine,
  RiShieldCheckLine,
  RiLockLine,
  RiLockUnlockLine,
  RiArrowLeftLine,
  RiExternalLinkLine,
} from "react-icons/ri";
import "./BusBookingConfirmation.css";

const BusBookingConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    bus,
    searchQuery,
    selectedSeats,
    passengerCount,
    passengerDetails,
    selectedBoardingPoint,
    selectedDroppingPoint,
    totalAmount,
    baseFare,
    convenienceFee,
    bookingStatus = "PAYMENT_SUCCESS",
    bookingId,
    paymentId,
    ticketKey,
    orderId,
    message,
    amount,
    bookingNo,
    pnr,
    supplierBookingId,
    invoiceNo,
    saleInvoiceNumber,
    purchaseInvoiceNumber,
    bookTicketKey,
    bookingType = "BOOK_DIRECTLY",
    showSuccessIcon = true,
  } = location.state || {};

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return `INR${Math.round(num).toLocaleString("en-IN")}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const options = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      };
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString("en-IN", options);
    } catch (error) {
      return dateString;
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} `;
  };

  const getStatusConfig = (status) => {
    const configs = {
      PAYMENT_SUCCESS: {
        icon: RiCheckboxCircleLine,
        title: "Payment Successful!",
        subtitle: "Your payment was successful. Booking is being processed.",
        theme: "success",
        textColor: "bbc-text-success",
        showActions: true,
        showTracking: true,
        showContactSupport: true,
        showPaymentButton: false,
      },
      CONFIRMED: {
        icon: RiCheckboxCircleLine,
        title: "Booking Confirmed!",
        subtitle: "Your bus tickets have been successfully booked",
        theme: "success",
        textColor: "bbc-text-success",
        showActions: true,
        showTracking: true,
        showContactSupport: false,
        showPaymentButton: false,
      },
      BLOCKED: {
        icon: RiLockLine,
        title: "Seats Blocked Successfully!",
        subtitle:
          "Your seats have been reserved. Complete payment to confirm booking.",
        theme: "warning",
        textColor: "bbc-text-warning",
        showActions: true,
        showTracking: true,
        showContactSupport: true,
        showPaymentButton: true,
      },
      PENDING: {
        icon: RiTimeLine,
        title: "Booking Processing",
        subtitle: "Your booking is being processed. We'll update you shortly.",
        theme: "info",
        textColor: "bbc-text-info",
        showActions: true,
        showTracking: false,
        showContactSupport: true,
        showPaymentButton: false,
      },
      PAYMENT_FAILED: {
        icon: RiCloseCircleLine,
        title: "Payment Failed",
        subtitle: "Payment was not completed. Please try again.",
        theme: "danger",
        textColor: "bbc-text-danger",
        showActions: true,
        showTracking: false,
        showContactSupport: false,
        showPaymentButton: true,
      },
      PAYMENT_CANCELLED: {
        icon: RiCloseCircleLine,
        title: "Payment Cancelled",
        subtitle:
          "Payment was cancelled. Please try again if you wish to proceed.",
        theme: "secondary",
        textColor: "bbc-text-secondary",
        showActions: true,
        showTracking: false,
        showContactSupport: false,
        showPaymentButton: true,
      },
      PAYMENT_SUCCESS_BOOKING_FAILED: {
        icon: RiErrorWarningLine,
        title: "Payment Successful - Booking Issue",
        subtitle:
          "Payment successful but booking failed. Our team will contact you.",
        theme: "warning",
        textColor: "bbc-text-warning",
        showActions: true,
        showTracking: true,
        showContactSupport: true,
        showPaymentButton: false,
      },
      PAYMENT_SUCCESS_BOOKING_PENDING: {
        icon: RiAlertLine,
        title: "Payment Successful - Processing",
        subtitle:
          "Payment successful but we encountered an issue. Please contact support.",
        theme: "warning",
        textColor: "bbc-text-warning",
        showActions: true,
        showTracking: true,
        showContactSupport: true,
        showPaymentButton: false,
      },
    };

    return configs[status] || configs.PAYMENT_SUCCESS;
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const printContent = document.getElementById("booking-details").innerHTML;
    const originalContent = document.body.innerHTML;

    document.body.innerHTML = printContent;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  const handleNewBooking = () => {
    navigate("/bus/search");
  };

  const handleViewBookings = () => {
    navigate("/bus/bookings");
  };

  const handleViewBlockedBookings = () => {
    navigate("/bus/blocked-bookings");
  };

  const handleCompletePayment = () => {
    navigate("/bus/complete-payment", {
      state: {
        bookingId,
        bookTicketKey,
        bus,
        searchQuery,
        selectedSeats,
        passengerCount,
        passengerDetails,
        selectedBoardingPoint,
        selectedDroppingPoint,
        totalAmount,
        baseFare,
        convenienceFee,
      },
    });
  };

  const handleContactSupport = () => {
    const supportPhone = "+91-XXXXXXXXXX";
    const supportEmail = "support@example.com";

    const message = `Booking Reference: ${bookingId || "N/A"}\nPayment ID: ${
      paymentId || "N/A"
    }\nStatus: ${bookingStatus}`;

    const whatsappUrl = `https://wa.me/${supportPhone.replace(
      /\D/g,
      ""
    )}?text=${encodeURIComponent(message)}`;
    const mailtoUrl = `mailto:${supportEmail}?subject=Booking Support - ${bookingId}&body=${encodeURIComponent(
      message
    )}`;

    const shouldCall = window.confirm(
      `Contact Support:\n\nPhone: ${supportPhone}\nEmail: ${supportEmail}\n\nClick OK to call, Cancel to email.`
    );

    if (shouldCall) {
      window.location.href = `tel:${supportPhone}`;
    } else {
      window.location.href = mailtoUrl;
    }
  };

  const statusConfig = getStatusConfig(bookingStatus);
  const StatusIcon = statusConfig.icon;
  const displayMessage = message || statusConfig.subtitle;

  return (
    <div id="layout-wrapper">
      <Header />

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            {/* Action Buttons - Top */}
            <div className="row mb-4 bbc-print-hide">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center bbc-flex-wrap bbc-gap-3">
                  <div>
                    <button
                      className="btn btn-outline-primary bbc-back-btn"
                      onClick={() => navigate("/bus/search")}
                    >
                      <RiArrowLeftLine />
                      Back to Search
                    </button>
                  </div>

                  <div className="d-flex bbc-flex-wrap bbc-gap-2 bbc-action-buttons">
                    {statusConfig.showActions && (
                      <>
                        <button
                          className="btn btn-secondary bbc-action-btn bbc-btn-secondary"
                          onClick={handlePrint}
                        >
                          <RiPrinterLine /> Print
                        </button>
                        <button
                          className="btn btn-success bbc-action-btn bbc-btn-success"
                          onClick={handleDownload}
                        >
                          <RiDownloadLine /> Download
                        </button>

                        {bookingStatus === "BLOCKED" ? (
                          <button
                            className="btn btn-primary bbc-action-btn bbc-btn-primary"
                            onClick={handleViewBlockedBookings}
                          >
                            View Blocked Bookings
                          </button>
                        ) : (
                          <button
                            className="btn btn-info bbc-action-btn bbc-btn-info"
                            onClick={handleViewBookings}
                          >
                            <RiFileList3Line /> View All Bookings
                          </button>
                        )}

                        <button
                          className="btn btn-primary bbc-action-btn bbc-btn-primary"
                          onClick={handleNewBooking}
                        >
                          <RiBusLine /> Book New Journey
                        </button>

                        {statusConfig.showContactSupport && (
                          <button
                            className="btn btn-warning bbc-action-btn bbc-btn-warning"
                            onClick={handleContactSupport}
                          >
                            <RiInformationLine /> Contact Support
                          </button>
                        )}

                        {statusConfig.showPaymentButton &&
                          bookingStatus === "BLOCKED" && (
                            <button
                              className="btn btn-success bbc-action-btn bbc-btn-success"
                              onClick={handleCompletePayment}
                            >
                              <RiLockUnlockLine /> Complete Payment
                            </button>
                          )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div id="booking-details">
              <div className="row justify-content-center">
                <div className="col-lg-12">
                  <div className="row">
                    {/* Status Banner */}
                    <div className="col-12 mb-4">
                      <div
                        className={`alert alert-${statusConfig.theme} border-0 shadow bbc-alert bbc-${statusConfig.theme}`}
                      >
                        <div className="row align-items-center">
                          <div className="col-md-8">
                            <div className="d-flex align-items-center bbc-flex-center bbc-gap-3">
                              <div className="bbc-status-icon-container">
                                <StatusIcon
                                  className={`fs-1 ${statusConfig.textColor}`}
                                />
                              </div>
                              <div className="flex-grow-1">
                                <h4
                                  className={`mb-1 ${statusConfig.textColor}`}
                                >
                                  {statusConfig.title}
                                </h4>
                                <p className="mb-2">{displayMessage}</p>

                                {/* Booking Reference Details */}
                                <div className="d-flex bbc-flex-wrap bbc-gap-3 mt-2">
                                  {bookingNo && (
                                    <div className="bbc-reference-item">
                                      <small className="bbc-text-muted d-block">
                                        Booking No
                                      </small>
                                      <strong>{bookingNo}</strong>
                                    </div>
                                  )}
                                  {pnr && (
                                    <div className="bbc-reference-item">
                                      <small className="bbc-text-muted d-block">
                                        PNR
                                      </small>
                                      <strong>{pnr}</strong>
                                    </div>
                                  )}
                                  {supplierBookingId && (
                                    <div className="bbc-reference-item">
                                      <small className="bbc-text-muted d-block">
                                        Supplier ID
                                      </small>
                                      <strong>{supplierBookingId}</strong>
                                    </div>
                                  )}
                                  {bookTicketKey &&
                                    bookingStatus === "BLOCKED" && (
                                      <div className="bbc-reference-item">
                                        <small className="bbc-text-muted d-block">
                                          Block Key
                                        </small>
                                        <strong className="bbc-text-warning">
                                          {bookTicketKey}
                                        </strong>
                                      </div>
                                    )}
                                </div>
                              </div>
                            </div>
                          </div>

                          {statusConfig.showTracking && (
                            <div className="col-md-4 text-md-end mt-3 mt-md-0">
                              <div className="d-flex flex-column bbc-gap-2">
                                <div className="alert alert-light bbc-alert bbc-secondary mb-0">
                                  <small className="bbc-text-muted">
                                    <RiShieldCheckLine className="me-1" />
                                    Your booking is secure
                                  </small>
                                </div>
                                {(bookingStatus === "PAYMENT_SUCCESS" ||
                                  bookingStatus === "BLOCKED") && (
                                  <div className="alert alert-info bbc-alert bbc-info mb-0">
                                    <small className="bbc-text-muted">
                                      <RiMailLine className="me-1" />
                                      Confirmation email sent
                                    </small>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Left Column - Journey & Passenger Details */}
                    <div className="col-lg-8">
                      {/* Journey Overview Card */}
                      {bus && searchQuery && (
                        <div className="card shadow-sm bbc-card mb-4">
                          <div className="card-header bg-white bbc-card-header">
                            <h5 className="mb-0 d-flex align-items-center bbc-flex-center bbc-gap-2">
                              <RiBusLine className="bbc-text-primary" />
                              Journey Overview
                            </h5>
                          </div>
                          <div className="card-body bbc-card-body">
                            <div className="row align-items-center bbc-mb-4">
                              <div className="col-md-5 text-center">
                                <div className="bbc-boarding-point-card">
                                  <h6 className="bbc-text-primary bbc-mb-2">
                                    {searchQuery.fromStation}
                                  </h6>
                                  <div className="bbc-time-display">
                                    {formatTime(bus.departureTime)}
                                  </div>
                                  <small className="bbc-text-muted">
                                    {selectedBoardingPoint?.location}
                                  </small>
                                </div>
                              </div>
                              <div className="col-md-2 text-center">
                                <RiArrowLeftRightLine className="bbc-text-muted fs-3 bbc-mb-2" />
                                <div className="bbc-duration-badge">
                                  {bus.duration || "Direct"}
                                </div>
                              </div>
                              <div className="col-md-5 text-center">
                                <div className="bbc-boarding-point-card">
                                  <h6 className="bbc-text-success bbc-mb-2">
                                    {searchQuery.toStation}
                                  </h6>
                                  <div className="bbc-time-display">
                                    {formatTime(bus.arrivalTime)}
                                  </div>
                                  <small className="bbc-text-muted">
                                    {selectedDroppingPoint?.location}
                                  </small>
                                </div>
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-md-6">
                                <div className="bbc-info-item">
                                  <RiCalendarLine className="bbc-text-muted me-2" />
                                  <div>
                                    <small className="bbc-text-muted">
                                      Travel Date
                                    </small>
                                    <p className="mb-0 bbc-fw-semibold">
                                      {formatDate(searchQuery.travelDate)}
                                    </p>
                                  </div>
                                </div>
                                <div className="bbc-info-item">
                                  <RiUserLine className="bbc-text-muted me-2" />
                                  <div>
                                    <small className="bbc-text-muted">
                                      Passengers
                                    </small>
                                    <p className="mb-0 bbc-fw-semibold">
                                      {passengerCount || 0} Passenger(s)
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="bbc-info-item">
                                  <RiFileList3Line className="bbc-text-muted me-2" />
                                  <div>
                                    <small className="bbc-text-muted">
                                      Bus Operator
                                    </small>
                                    <p className="mb-0 bbc-fw-semibold">
                                      {bus.operatorName}
                                    </p>
                                  </div>
                                </div>
                                <div className="bbc-info-item">
                                  <RiBusLine className="bbc-text-muted me-2" />
                                  <div>
                                    <small className="bbc-text-muted">
                                      Bus Type
                                    </small>
                                    <p className="mb-0 bbc-fw-semibold">
                                      {bus.busType || "Standard"}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Boarding & Dropping Points */}
                      {(selectedBoardingPoint || selectedDroppingPoint) && (
                        <div className="card shadow-sm bbc-card mb-4">
                          <div className="card-header bg-white bbc-card-header">
                            <h5 className="mb-0 d-flex align-items-center bbc-flex-center bbc-gap-2">
                              <RiMapPinLine className="bbc-text-primary" />
                              Pickup & Drop Points
                            </h5>
                          </div>
                          <div className="card-body bbc-card-body">
                            <div className="row">
                              {selectedBoardingPoint && (
                                <div className="col-md-6">
                                  <div className="bbc-point-details">
                                    <h6 className="bbc-text-primary bbc-mb-2">
                                      Boarding Point
                                    </h6>
                                    <p className="bbc-fw-semibold bbc-mb-1">
                                      {selectedBoardingPoint.location}
                                    </p>
                                    <p className="bbc-text-muted small bbc-mb-1">
                                      <RiTimeLine className="me-1" />
                                      {formatTime(selectedBoardingPoint.time)}
                                    </p>
                                    <p className="bbc-text-muted small bbc-mb-0">
                                      {selectedBoardingPoint.address ||
                                        selectedBoardingPoint.landmark ||
                                        selectedBoardingPoint.pointName}
                                    </p>
                                  </div>
                                </div>
                              )}
                              {selectedDroppingPoint && (
                                <div className="col-md-6">
                                  <div className="bbc-point-details">
                                    <h6 className="bbc-text-success bbc-mb-2">
                                      Dropping Point
                                    </h6>
                                    <p className="bbc-fw-semibold bbc-mb-1">
                                      {selectedDroppingPoint.location}
                                    </p>
                                    <p className="bbc-text-muted small bbc-mb-1">
                                      <RiTimeLine className="me-1" />
                                      {formatTime(selectedDroppingPoint.time)}
                                    </p>
                                    <p className="bbc-text-muted small bbc-mb-0">
                                      {selectedDroppingPoint.address ||
                                        selectedDroppingPoint.landmark ||
                                        selectedDroppingPoint.pointName}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Passenger Details */}
                      {selectedSeats &&
                        passengerDetails &&
                        passengerDetails.length > 0 && (
                          <div className="card shadow-sm bbc-card">
                            <div className="card-header bg-white bbc-card-header">
                              <h5 className="mb-0 d-flex align-items-center bbc-flex-center bbc-gap-2">
                                <RiUserLine className="bbc-text-success" />
                                Passenger Details
                              </h5>
                            </div>
                            <div className="card-body bbc-card-body">
                              <div className="bbc-table-responsive">
                                <table className="table table-hover bbc-passenger-table">
                                  <thead className="table-light">
                                    <tr>
                                      <th>Seat No</th>
                                      <th>Passenger Name</th>
                                      <th>Age</th>
                                      <th>ID Proof</th>
                                      <th>Bus Fare</th>
                                      <th>Conv. Fee</th>
                                      <th>Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {selectedSeats.map((seat, index) => {
                                      const seatBaseFare =
                                        seat.totalPriceWithTaxes || 0;
                                      const seatConvFee =
                                        seat.convenienceFee || 100;
                                      const seatTotal =
                                        seatBaseFare + seatConvFee;
                                      const passenger =
                                        passengerDetails[index] ||
                                        passengerDetails[0];

                                      return (
                                        <tr key={seat.id}>
                                          <td>
                                            <span className="bbc-seat-badge">
                                              {seat.id}
                                            </span>
                                          </td>
                                          <td>
                                            <strong>
                                              {passenger?.title || "Mr/Ms"}{" "}
                                              {passenger?.firstName}{" "}
                                              {passenger?.lastName}
                                            </strong>
                                            <br />
                                            <small className="bbc-text-muted">
                                              {passenger?.email || "N/A"}
                                            </small>
                                          </td>
                                          <td>
                                            {passenger?.age || "N/A"} years
                                          </td>
                                          <td>
                                            <div>
                                              <small className="bbc-text-muted d-block">
                                                {passenger?.idProof || "N/A"}
                                              </small>
                                              {passenger?.idProofNumber && (
                                                <small className="bbc-text-muted d-block">
                                                  ID:{" "}
                                                  {passenger.idProofNumber.slice(
                                                    0,
                                                    8
                                                  )}
                                                  ...
                                                </small>
                                              )}
                                            </div>
                                          </td>
                                          <td>
                                            {formatCurrency(seatBaseFare)}
                                          </td>
                                          <td>{formatCurrency(seatConvFee)}</td>
                                          <td>
                                            <strong>
                                              {formatCurrency(seatTotal)}
                                            </strong>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        )}
                    </div>

                    {/* Right Column - Summary & Actions */}
                    <div className="col-lg-4">
                      {/* Fare Summary */}
                      {totalAmount && (
                        <div className="card shadow-sm bbc-card mb-4">
                          <div className="card-header bg-white bbc-card-header">
                            <h5 className="mb-0 d-flex align-items-center bbc-flex-center bbc-gap-2">
                              <RiMoneyDollarCircleLine className="bbc-text-info" />
                              Fare Summary
                            </h5>
                          </div>
                          <div className="card-body bbc-card-body">
                            <div className="bbc-fare-summary">
                              <div className="bbc-fare-item">
                                <div className="bbc-fare-label">
                                  <span>Bus Fare</span>
                                </div>
                                <div className="bbc-fare-amount">
                                  {formatCurrency(baseFare)}
                                </div>
                              </div>

                              <div className="bbc-fare-item">
                                <div className="bbc-fare-label">
                                  <span>Convenience Fee</span>
                                  <small className="bbc-text-muted d-block">
                                    (INR100 × {passengerCount || 1})
                                  </small>
                                </div>
                                <div className="bbc-fare-amount">
                                  {formatCurrency(convenienceFee)}
                                </div>
                              </div>

                              <div className="bbc-fare-divider"></div>

                              <div className="bbc-fare-item bbc-total">
                                <div className="bbc-fare-label">
                                  <strong>Total Amount</strong>
                                </div>
                                <div className="bbc-fare-amount">
                                  <strong className="bbc-text-primary fs-5">
                                    {formatCurrency(totalAmount)}
                                  </strong>
                                </div>
                              </div>
                            </div>

                            {/* Payment Status */}
                            <div
                              className={`bbc-alert bbc-${statusConfig.theme} mt-3`}
                            >
                              <div className="d-flex align-items-center bbc-flex-center bbc-gap-2">
                                <StatusIcon />
                                <div>
                                  <strong className="d-block">
                                    Status: {bookingStatus.replace(/_/g, " ")}
                                  </strong>
                                  {paymentId && (
                                    <small className="d-block bbc-text-muted">
                                      Payment ID: {paymentId}
                                    </small>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusBookingConfirmation;
